/**
 * Internal (non-public) postMessage `type` values used ONLY for
 * loader<->embed-app handshakes that need to move something out of the
 * iframe URL and into a validated postMessage exchange instead:
 * - The tenant's `PublicApiKey` (see `@vivian/loader`'s
 *   `use-vivian-element.ts` and `@vivian/embed-app`'s
 *   `App.tsx`/`messaging/apiKeyChannel.ts`).
 * - F7.1: the host's theme tokens (`--vivian-color-primary`/`--vivian-radius`/
 *   `--vivian-font-family`), which can never reach the iframe's document via
 *   plain CSS custom-property inheritance the way they reach `<use-vivian>`'s
 *   OWN shadow DOM (a cross-origin iframe is a separate document — see
 *   `@vivian/loader`'s `use-vivian-element.ts` for the host-side send and
 *   `@vivian/embed-app`'s `messaging/themeChannel.ts` for the receive side).
 *
 * Deliberately NOT part of {@link VIVIAN_EVENTS} (`events.ts`) — these never
 * become `vivian:*` CustomEvents on the host element. Both
 * `@vivian/loader`'s `message-relay.ts` (iframe -> host direction) and
 * `@vivian/embed-app`'s `apiKeyChannel.ts`/`themeChannel.ts` (host -> iframe
 * direction) intercept a message carrying one of these `type`s BEFORE it
 * would ever reach the public-event vocabulary check, specifically so a
 * host page can never observe (or spoof, past the existing origin+source
 * validation) this internal handshake as a public event.
 */
/**
 * Sent by the embed app (iframe) to the host page once it has mounted and
 * registered its own postMessage listener — signals "I'm ready to receive
 * the API key, if you have one". Carries no payload.
 */
export const EMBED_READY_MESSAGE_TYPE = "embed-ready";
/**
 * Sent by the host page (loader) to the embed app (iframe) in reply to
 * {@link EMBED_READY_MESSAGE_TYPE}, ONLY when `<use-vivian api-key="...">`
 * was actually set. `payload.apiKey` is the tenant's `PublicApiKey` — this
 * is the ONE place that value travels after the F7 fix (never the iframe
 * URL's query string — see `use-vivian-element.ts`'s `apiKey` getter for
 * why that leaked it to browser history / Referer / CDN access logs).
 */
export const API_KEY_MESSAGE_TYPE = "api-key";
/**
 * F7.1 (tema do lojista): sent by the host page (loader), in reply to the
 * SAME {@link EMBED_READY_MESSAGE_TYPE} signal the `api-key` reply above
 * already reuses, carrying whatever `ThemeTokens` (`theme.ts`) the host
 * declared as CSS custom properties on `<use-vivian>` itself — see
 * `use-vivian-element.ts`'s reader for exactly which properties. Only sent
 * when at least one token was actually declared (same "absence is normal,
 * nothing to reply with" posture `api-key` already has when
 * `<use-vivian>` never set that attribute) — a host that themes nothing
 * never receives this message at all, and the embed app's own CSS
 * `var(--vivian-*, <default>)` fallbacks render exactly as they did before
 * this feature existed.
 */
export const THEME_MESSAGE_TYPE = "theme";
//# sourceMappingURL=handshake.js.map